/**
 * 🎁 **The admin's give box** — protocol 47. `T` opens it, for an admin only.
 *
 * 🎯 **haugur, 2026-10-01**, after starving mid-test and losing the kit with
 * everything else: *"which is annoying when one is test alot"* — and he chose
 * a give command. **`T` because it is Minecraft's chat key** and nothing here
 * used it; `/` is Shift+7 on an Icelandic keyboard.
 *
 * 🔴 **This box only decides what to ASK for.** The typed line is read by
 * `parseGive` in `@lofi/shared`, so a name it cannot place is answered here at
 * once — but whether the asker is an admin is the server's question, and it
 * answers it from the database, not from this file.
 *
 * ⚠️ **Keys typed here must not reach the game**, exactly as in the bug box:
 * typing *"steinöxi"* must not open the pack on its E.
 */

import { GIVE_MAX, parseGive, itemIdForKey, itemById, type GiveRequest } from '@lofi/shared';

export class GiveBox {
  private readonly root: HTMLDivElement;
  private readonly input: HTMLInputElement;
  private readonly onOpenChanged: (open: boolean) => void;

  /**
   * @param send asks the server for an item that `parseGive` understood
   * @param say shows a sentence the way every other notice is shown
   * @param words the placeholder and the three sentences, already translated
   */
  constructor(
    send: (item: string, count: number) => void,
    say: (text: string) => void,
    words: {
      readonly placeholder: string;
      readonly unknown: (name: string) => string;
      readonly badCount: (max: number) => string;
      readonly asked: (count: number, item: string) => string;
    },
    onOpenChanged: (open: boolean) => void,
  ) {
    this.onOpenChanged = onOpenChanged;
    this.root = document.createElement('div');
    this.root.className = 'givebox';
    this.root.hidden = true;
    this.input = document.createElement('input');
    this.input.type = 'text';
    this.input.maxLength = 80;
    this.input.placeholder = words.placeholder;
    this.input.autocomplete = 'off';
    this.input.spellcheck = false;
    this.root.append(this.input);
    document.body.appendChild(this.root);

    this.root.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const line = this.input.value;
        const asked: GiveRequest = parseGive(line);
        if (asked.ok) {
          send(asked.item, asked.count);
          const name = itemById(itemIdForKey(asked.item) ?? -1)?.nameIs ?? asked.item;
          say(words.asked(asked.count, name));
          this.close();
        } else if (asked.reason === 'badCount') {
          say(words.badCount(GIVE_MAX));
        } else if (asked.reason === 'unknownItem') {
          say(words.unknown(line.trim()));
        } else {
          this.close();
        }
      }
      /* The game listens on `document` for E, O and the rest. */
      e.stopPropagation();
    });
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  open(): void {
    this.input.value = '';
    this.root.hidden = false;
    this.onOpenChanged(true);
    this.input.focus();
  }

  close(): void {
    if (this.root.hidden) return;
    this.root.hidden = true;
    this.onOpenChanged(false);
  }
}
